import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, Empty, Form, Input, Modal, Select, Space, Table, Tag, Tooltip, message,
} from 'antd';
import { EditOutlined, FilePdfOutlined, ReloadOutlined } from '@ant-design/icons';
import { testDocAPI } from '../../services/api';
import { CaseSpecRow, PlanRow, SiteTag, downloadBlob } from './common';
import { gray, font } from '../../theme/tokens';

/* 테스트 케이스 명세 탭
   239건을 손으로 쓸 수 없으니 자동 시드하고, 다듬은 행만 '수정됨'으로 표시해
   재시드 때 보존한다. 여기서 편집한 문안이 케이스 명세서 PDF 에 그대로 실린다. */

interface Props { bodyHeight: number; canWrite: boolean; }

const PRIORITY_COLOR: Record<string, string> = { '상': '#dc2626', '중': '#f59e0b', '하': '#94a3b8' };

const CaseSpecTab: React.FC<Props> = ({ bodyHeight, canWrite }) => {
  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [planId, setPlanId] = useState<number | null>(null);
  const [rows, setRows] = useState<CaseSpecRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [site, setSite] = useState<string | undefined>();
  const [priority, setPriority] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [edit, setEdit] = useState<CaseSpecRow | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    (async () => {
      try {
        const res = await testDocAPI.plans();
        const items: PlanRow[] = res.data.items || [];
        setPlans(items);
        setPlanId(res.data.active_plan_id ?? (items[0]?.id ?? null));
      } catch { /* 권한 없으면 조용히 */ }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!planId) { setRows([]); return; }
    setLoading(true);
    try {
      const res = await testDocAPI.cases(planId, { site, priority, search: search || undefined });
      setRows(res.data.items || []);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '케이스를 불러오지 못했습니다.');
    } finally { setLoading(false); }
  }, [planId, site, priority, search]);

  useEffect(() => { load(); }, [load]);

  const doSave = async () => {
    try {
      const v = await form.validateFields();
      await testDocAPI.updateCase(edit!.id, v);
      message.success('케이스 명세가 수정되었습니다.');
      setEdit(null);
      load();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || '수정에 실패했습니다.');
    }
  };

  const plan = plans.find(p => p.id === planId);

  const columns = [
    { title: '케이스 번호', dataIndex: 'case_no', width: 175,
      render: (v: string, r: CaseSpecRow) => (
        <Space size={5}>
          <span style={{ fontFamily: font.mono, fontSize: 11.5 }}>{v}</span>
          {r.is_custom && <Tooltip title="손으로 다듬음 — 재시드해도 보존됩니다">
            <Tag color="purple" style={{ margin: 0, fontSize: 10 }}>수정됨</Tag></Tooltip>}
        </Space>
      ) },
    { title: '사이트', dataIndex: 'site', width: 78, render: (v: string) => <SiteTag site={v} /> },
    { title: '화면', dataIndex: 'screen_name', width: 155,
      render: (v: string, r: CaseSpecRow) => (
        <Tooltip title={r.screen_path}><span style={{ fontWeight: 600 }}>{v}</span></Tooltip>
      ) },
    { title: '검사 항목', dataIndex: 'check_name', width: 175 },
    { title: '우선', dataIndex: 'priority', width: 62,
      render: (v: string) => (
        <Tag style={{ margin: 0, color: PRIORITY_COLOR[v], borderColor: PRIORITY_COLOR[v], fontWeight: 700 }}>{v}</Tag>
      ) },
    { title: '수행', dataIndex: 'exec_type', width: 70,
      render: (v: string) => <span style={{ fontSize: 12, color: gray[500] }}>{v === 'auto' ? '자동' : '수동'}</span> },
    { title: '절차', dataIndex: 'procedure', ellipsis: true,
      render: (v: string) => (
        <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{v}</span>}>
          <span style={{ fontSize: 12, color: gray[600] }}>{(v || '').split('\n')[0]}</span>
        </Tooltip>
      ) },
    { title: '기대결과', dataIndex: 'expected', ellipsis: true,
      render: (v: string) => (
        <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{v}</span>}>
          <span style={{ fontSize: 12, color: gray[600] }}>{v}</span>
        </Tooltip>
      ) },
    ...(canWrite ? [{
      title: '', width: 60, fixed: 'right' as const,
      render: (_: any, r: CaseSpecRow) => (
        <Button size="small" type="text" icon={<EditOutlined />} onClick={() => {
          setEdit(r);
          form.setFieldsValue({
            precondition: r.precondition, procedure: r.procedure, expected: r.expected,
            priority: r.priority, exec_type: r.exec_type, note: r.note,
          });
        }} />
      ),
    }] : []),
  ];

  if (!plans.length) {
    return <Empty style={{ padding: 60 }} image={Empty.PRESENTED_IMAGE_SIMPLE}
      description="테스트 계획서를 먼저 작성하세요. 케이스 명세는 계획서에 딸려 자동 생성됩니다." />;
  }

  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <Select style={{ width: 320 }} value={planId ?? undefined} onChange={setPlanId}
          options={plans.map(p => ({ value: p.id, label: `${p.doc_no} v${p.version} — ${p.title}` }))} />
        <Select allowClear placeholder="사이트" style={{ width: 120 }} value={site} onChange={setSite}
          options={[{ value: 'lab', label: 'lab' }, { value: 'partner', label: 'partner' }]} />
        <Select allowClear placeholder="우선순위" style={{ width: 110 }} value={priority} onChange={setPriority}
          options={['상', '중', '하'].map(v => ({ value: v, label: v }))} />
        <Input.Search allowClear placeholder="화면·검사·번호 검색" style={{ width: 230 }}
          onSearch={setSearch} onChange={e => { if (!e.target.value) setSearch(''); }} />
        <Button icon={<ReloadOutlined />} onClick={load} />
        <Button icon={<FilePdfOutlined />} disabled={!planId} onClick={async () => {
          try {
            const res = await testDocAPI.casesPdf(planId!);
            downloadBlob(res, `${plan?.doc_no || 'cases'}_cases.pdf`);
          } catch { message.error('PDF를 만들지 못했습니다.'); }
        }}>명세서 PDF</Button>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: gray[400], alignSelf: 'center' }}>
          총 {rows.length}건 · 문안을 다듬으면 재시드해도 보존됩니다
        </span>
      </div>

      <Table<CaseSpecRow>
        rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns as any}
        scroll={{ y: bodyHeight, x: 1350 }}
        rowClassName={(_, i) => (i % 2 ? 'table-row-alt' : '')}
        pagination={{
          pageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200'],
          showTotal: (t, r) => `${r[0]}–${r[1]} / 총 ${t}건`, size: 'small',
        }}
      />

      <Modal open={!!edit} onCancel={() => setEdit(null)} onOk={doSave} okText="저장" cancelText="취소"
        width={720} title={edit && (
          <Space>
            <span style={{ fontFamily: font.mono, fontSize: 13 }}>{edit.case_no}</span>
            <span style={{ fontWeight: 400, color: gray[500], fontSize: 13 }}>
              [{edit.site}] {edit.screen_name} · {edit.check_name}
            </span>
          </Space>
        )}>
        <Form form={form} layout="vertical" size="small" style={{ marginTop: 8 }}>
          <Space size={12}>
            <Form.Item name="priority" label="우선순위" style={{ width: 120 }}>
              <Select options={['상', '중', '하'].map(v => ({ value: v, label: v }))} />
            </Form.Item>
            <Form.Item name="exec_type" label="수행 구분" style={{ width: 140 }}>
              <Select options={[{ value: 'auto', label: '자동' }, { value: 'manual', label: '수동' }]} />
            </Form.Item>
          </Space>
          <Form.Item name="precondition" label="사전조건"><Input.TextArea rows={3} /></Form.Item>
          <Form.Item name="procedure" label="절차"><Input.TextArea rows={4} /></Form.Item>
          <Form.Item name="expected" label="기대결과"><Input.TextArea rows={3} /></Form.Item>
          <Form.Item name="note" label="비고"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CaseSpecTab;
